import { useEffect, useRef, useState } from 'react';

const STORY_MP3_MAP: Record<string, string> = {
  egocrisis: 'EgoCrisis',
  foundation_gamble: 'FoundationGamble',
  data_storm: 'DataStorm',
  cloud_heist: 'CloudHeist',
  neural_override: 'NeuralOverride',
  pipeline_inferno: 'PipelineInferno',
  merge_conflict: 'MergeConflict',
  deadline_paradox: 'DeadlineParadox',
  quantum_gambit: 'QuantumGambit',
  arabic_modern: 'ArabicModern',
  learning_machine: 'LearningMachine',
  ink_protocol: 'InkProtocol',
};

const BASE_URL = 'https://ccpubmedia.blob.core.windows.net/music';

export function useMissionMusic(storyKey: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.3);
  // currentTime and duration kept as refs to avoid re-renders on every tick
  const currentTimeRef = useRef(0);
  const durationRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;

    const fileName = STORY_MP3_MAP[storyKey];
    if (!fileName) return;

    const url = `${BASE_URL}/${fileName}.mp3`;
    const audio = new Audio(url);
    audio.volume = 0.3;
    audio.loop = true;
    audioRef.current = audio;

    const handleTimeUpdate = () => { currentTimeRef.current = audio.currentTime; };
    const handleLoadedMetadata = () => { durationRef.current = audio.duration; };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleError = () => {
      console.warn('[useMissionMusic] Audio failed to load:', url);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    audio.play().catch((err: Error) => {
      console.warn('[useMissionMusic] Could not play audio:', err.message);
    });

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
      setIsPlaying(false);
      currentTimeRef.current = 0;
      durationRef.current = 0;
    };
  }, [storyKey]);

  const toggleMute = () => {
    setIsMuted(prev => {
      const next = !prev;
      if (audioRef.current) {
        audioRef.current.muted = next;
      }
      return next;
    });
  };

  const play = () => {
    audioRef.current?.play().catch((err: Error) => {
      console.warn('[useMissionMusic] play error:', err.message);
    });
  };

  const pause = () => {
    audioRef.current?.pause();
  };

  const seek = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      currentTimeRef.current = seconds;
    }
  };

  const setVolume = (v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (audioRef.current) {
      audioRef.current.volume = clamped;
    }
  };

  return {
    isMuted,
    toggleMute,
    isPlaying,
    volume,
    play,
    pause,
    seek,
    setVolume,
  };
}
